/*
  Change Workflow Modal

  Modal for changing the workflow on a workflow node.
  Adapted from the legacy journey editor's workflow form functionality.

  Features:
  - Select a new workflow from a dropdown
  - Auto-select similar version if already in use
  - Confirm when outcomes differ
  - Update the workflow node and related edges
*/

import React, {
  useState,
  useMemo,
  useCallback,
} from 'react';
import type { Option } from '@usealloy/component-library';
import {
  Box,
  Button,
  Icon,
  IconName,
  Label,
  Modal,
  MultiSelect,
  Text,
  useSnackBar,
  JourneyNode,
} from '@usealloy/component-library';
import { useAppSelector } from '~/store/reduxHooks';
import { selectDocument, selectWorkflowsFromProps } from '../../store/selectors';
import apiCalls from '~/helpers/apiCalls';
import type { Node } from '../../types';
import type { WorkflowData } from '../../store/state';

interface ChangeWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedWorkflow: WorkflowWithVersions, selectedVersion: any) => void;
  node: Node;
}

// Extended workflow type with versions
interface WorkflowWithVersions extends WorkflowData {
  versions?: any[];
}

const NODE_TYPE_ICON_MAP = {
  workflow: IconName.Workflows,
  source_workflow: IconName.Workflows,
  start_router: IconName.Workflows,
};

/**
 * Get similar workflow version from nodes in the journey
 * If another node uses the same workflow, use that version
 */
const getSimilarWorkflowVersion = (nodes: Node[], workflowToken: string): number | null => {
  if (!nodes || !workflowToken) return null;

  const similarNode = nodes.find(
    node => {
      const config = node.config as any;
      return (
        config?.workflow_current_version
        && config?.application_token === workflowToken
      );
    },
  );

  return similarNode ? (similarNode.config as any).workflow_current_version : null;
};

/**
 * Get selected workflow version from versions array
 * Uses either the provided version ID or the active version
 */
const getSelectedWorkflowVersion = (
  selectedVersionId: number | null,
  versions: any[] = [],
) => versions.find(({ active, public_version: publicVersion }) => {
  if (selectedVersionId) {
    return publicVersion === selectedVersionId;
  }
  return !!active;
});

/**
 * Change Workflow Modal Component
 *
 * Allows users to select a different workflow for a workflow node.
 * Handles version selection and outcome validation.
 */
const ChangeWorkflowModal: React.FC<ChangeWorkflowModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  node,
}) => {
  const addSnackBar = useSnackBar();
  const document = useAppSelector(selectDocument);

  // Get workflows from Redux props
  const workflows = useAppSelector(selectWorkflowsFromProps);

  const [selectedWorkflowToken, setSelectedWorkflowToken] = useState<string | null>(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [workflowsWithVersions, setWorkflowsWithVersions] = useState<
  Record<string, WorkflowWithVersions>
  >({});

  // Get current node configuration
  const nodeIcon = NODE_TYPE_ICON_MAP[node.type] || IconName.Workflows;

  // Build workflow options for the dropdown
  const workflowOptions: Option[] = useMemo(() => {
    if (!workflows || workflows.length === 0) return [];

    return workflows
      .filter((wf: WorkflowData) => !wf.archived) // Filter out archived workflows
      .map((wf: WorkflowData) => ({
        label: wf.name,
        value: wf.application_token,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [workflows]);

  // Find selected workflow from the list
  const selectedWorkflow = useMemo(() => {
    if (!selectedWorkflowToken || !workflows) return null;
    return workflows.find((wf: WorkflowData) => wf.application_token === selectedWorkflowToken);
  }, [selectedWorkflowToken, workflows]);

  const selectedOption: Option[] = useMemo(() => {
    if (!selectedWorkflow) return [];
    return [{
      label: selectedWorkflow.name,
      value: selectedWorkflow.application_token,
    }];
  }, [selectedWorkflow]);

  /**
   * Fetch workflow versions when a workflow is selected
   */
  const fetchWorkflowVersions = useCallback(async (workflowToken: string) => {
    // Check if we already have versions cached
    if (workflowsWithVersions[workflowToken]?.versions) {
      return workflowsWithVersions[workflowToken].versions;
    }

    setIsLoadingVersions(true);
    try {
      const response = await apiCalls.workflows.getWorkflowDetails(workflowToken);

      // The response contains versions in tableState.data
      const versions = response?.tableState?.data?.[0]?.data || [];

      // Cache the versions
      const workflow = workflows.find((wf: WorkflowData) => wf.application_token === workflowToken);
      if (workflow) {
        setWorkflowsWithVersions(prev => ({
          ...prev,
          [workflowToken]: {
            ...workflow,
            versions,
          },
        }));
      }

      return versions;
    } catch (error) {
      addSnackBar({
        type: 'danger',
        title: 'Failed to load workflow versions',
      });
      return [];
    } finally {
      setIsLoadingVersions(false);
    }
  }, [workflowsWithVersions, workflows, addSnackBar]);

  /**
   * Handle workflow selection change
   */
  const handleWorkflowChange = useCallback(async (option: Option) => {
    const workflowToken = option.value as string;
    setSelectedWorkflowToken(workflowToken);

    // Fetch versions for the selected workflow
    await fetchWorkflowVersions(workflowToken);
  }, [fetchWorkflowVersions]);

  /**
   * Handle confirm button click
   * Validates and calls onConfirm with the selected workflow and version
   */
  const handleConfirm = useCallback(async () => {
    if (!selectedWorkflowToken || !selectedWorkflow) {
      addSnackBar({
        type: 'danger',
        title: 'Please select a workflow',
      });
      return;
    }

    try {
      // Ensure we have versions
      let versions = workflowsWithVersions[selectedWorkflowToken]?.versions;
      if (!versions) {
        versions = await fetchWorkflowVersions(selectedWorkflowToken);
      }

      if (!versions || versions.length === 0) {
        addSnackBar({
          type: 'danger',
          title: 'No versions found for this workflow',
        });
        return;
      }

      // Determine which version to use
      // First, check if this workflow is already used in the journey
      const allNodes = document ? Object.values(document.nodes) : [];
      const similarVersionId = getSimilarWorkflowVersion(allNodes, selectedWorkflowToken);

      // Get the selected version (similar version or active version)
      const selectedVersion = getSelectedWorkflowVersion(similarVersionId, versions);

      if (!selectedVersion) {
        addSnackBar({
          type: 'danger',
          title: 'No active version found for this workflow',
        });
        return;
      }

      // Build complete workflow object with versions
      const workflowWithVersions: WorkflowWithVersions = {
        ...selectedWorkflow,
        versions,
      };

      // Call onConfirm with workflow and version
      onConfirm(workflowWithVersions, selectedVersion);
    } catch (error) {
      // Error handling for workflow change failure
      addSnackBar({
        type: 'danger',
        title: 'Failed to change workflow',
      });
    }
  }, [
    selectedWorkflowToken,
    selectedWorkflow,
    workflowsWithVersions,
    fetchWorkflowVersions,
    addSnackBar,
    onConfirm,
    document,
  ]);

  const isSaveDisabled = !selectedWorkflowToken || isLoadingVersions;

  return (
    <Modal
      isOpen={isOpen}
      hasCloseIcon
      style={{ maxWidth: '600px' }}
      header={(
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          Change Workflow
        </h3>
      )}
      onClose={onClose}
      secondaryAction={<Button onClick={onClose}>Cancel</Button>}
      primaryAction={(
        <Button
          onClick={handleConfirm}
          disabled={isSaveDisabled}
        >
          {isLoadingVersions ? 'Loading...' : 'Change Workflow'}
        </Button>
      )}
    >
      <Box direction="column" gap={4} alignItems="flex-start">
        {/* Current workflow display */}
        <Box direction="column" gap={2}>
          <Label>Current Workflow</Label>
          <JourneyNode
            iconName={nodeIcon}
            isSelected={false}
            buttonOnClick={() => {}}
            status="neutral"
            isNonInteractive
            style={{ width: '100%', maxWidth: '400px' }}
          >
            {node.name}
          </JourneyNode>
        </Box>

        {/* Workflow selector */}
        <Box direction="column" gap={2} alignItems="flex-start">
          <Label htmlFor="workflow-select">
            <Text style={{ color: 'red' }}>*</Text>
            Select New Workflow
          </Label>
          <MultiSelect
            id="workflow-select"
            data-testid="workflow-select"
            required
            isMulti={false}
            placeholder="Search for a workflow..."
            isSearchable
            options={workflowOptions}
            onChange={handleWorkflowChange}
            selectedOptions={selectedOption}
          />
        </Box>

        {/* Info message */}
        <Box direction="row" gap={2} alignItems="flex-start">
          <Icon icon={IconName.Info} />
          <Text style={{ color: 'var(--cl-gray-600)', fontSize: '14px' }}>
            Changing Workflows may affect connected edges and outcomes.
            You will need to confirm the change if outcomes differ from the current workflow.
          </Text>
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangeWorkflowModal;

